import { useEffect, useState } from "react";
import { Pause, Play, Search } from "lucide-react";
import LoadingSpinner from "@/components/LoadingSpinner";
import api from "../../../utils/axios-utils";
import { isDemoAccount, loadDemoUsageControl } from "../service/demoAnalytics";

interface UsageStudent {
  user_id: number;
  student_name: string;
  classrooms: string[];
  active_tutoring_time_ms: number;
  lessons_completed: number;
  is_active: boolean;
}

interface TeacherQuota {
  max_usage_allowed_ms: number;
  current_usage_ms: number;
  remaining_ms: number;
}

interface SortConfig {
  key: "student_name" | "active_tutoring_time_ms";
  direction: "asc" | "desc";
}

const formatDuration = (ms: number): string => {
  const minutes = Math.floor(ms / 60000);
  return `${minutes} min`;
};

const formatHours = (ms: number): string => {
  const hours = Math.floor(ms / 3600000);
  return `${hours}h`;
};

const SortIcon = ({
  columnKey,
  sortConfig,
}: {
  columnKey: SortConfig["key"];
  sortConfig: SortConfig;
}) => (
  <span className="et-usage-sort" aria-hidden="true">
    {sortConfig.key === columnKey
      ? sortConfig.direction === "asc"
        ? "↑"
        : "↓"
      : "↕"}
  </span>
);

const ThresholdMarker = ({ value }: { value: number }) => (
  <div
    className="absolute top-6 h-8 w-0.5 z-10"
    style={{
      left: `${value}%`,
      backgroundImage:
        "linear-gradient(to bottom, var(--et-muted) 50%, transparent 50%)",
      backgroundSize: "2px 8px",
      backgroundRepeat: "repeat-y",
    }}
  >
    <span className="absolute -top-5 text-sm text-[var(--et-muted)] -translate-x-1/2">
      {value}%
    </span>
  </div>
);

const ProgressBarSkeleton = () => (
  <div className="flex flex-col gap-2 animate-pulse">
    <div className="relative pt-6">
      <div className="w-full h-8 bg-[#ede8dc] rounded-xl"></div>
    </div>
    <div className="flex items-center justify-between text-sm">
      <div className="h-4 w-48 bg-[#ede8dc] rounded"></div>
      <div className="flex items-center gap-6">
        <div className="h-4 w-40 bg-[#ede8dc] rounded"></div>
        <div className="h-4 w-40 bg-[#ede8dc] rounded"></div>
      </div>
    </div>
  </div>
);

const UsageControlViewContent = () => {
  const [students, setStudents] = useState<UsageStudent[]>([]);
  const [quota, setQuota] = useState<TeacherQuota | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "student_name",
    direction: "asc",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {
          search: search || undefined,
          sort_by: sortConfig.key,
          sort_order: sortConfig.direction,
        };
        // Demo accounts read a frozen capture instead — see service/demoAnalytics.
        const data = isDemoAccount("usage-control")
          ? await loadDemoUsageControl(params)
          : (await api.get("/analytics/usage-control", { params })).data;
        setStudents(data.students || []);
        setQuota(data.quota || null);
      } catch (error) {
        console.error("Failed to fetch usage control data:", error);
        setStudents([]);
        setQuota(null);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchData, 300);
    return () => clearTimeout(debounce);
  }, [search, sortConfig]);

  const handleSort = (key: SortConfig["key"]) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleStatusToggle = async (userId: number, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    try {
      setStudents((prev) =>
        prev.map((s) =>
          s.user_id === userId ? { ...s, is_active: newStatus } : s,
        ),
      );
      // The optimistic update above is the whole effect in demo mode: this
      // endpoint writes student.is_active, and the demo students are real rows.
      if (!isDemoAccount("usage-control")) {
        await api.patch(`/analytics/students/${userId}/status`, {
          is_active: newStatus,
        });
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      setStudents((prev) =>
        prev.map((s) =>
          s.user_id === userId ? { ...s, is_active: currentStatus } : s,
        ),
      );
    }
  };

  // Calculate usage percentage for progress bar
  const usagePercentage =
    quota && quota.max_usage_allowed_ms > 0
      ? Math.min(
          100,
          (quota.current_usage_ms / quota.max_usage_allowed_ms) * 100,
        )
      : 0;
  const isOverLimit = quota ? quota.remaining_ms <= 0 : false;

  return (
    <div className="et-usage-view">
      <header className="et-analytics-greeting">
        <h1>Usage Control</h1>
      </header>

      {/* Usage Progress Bar */}
      {loading ? (
        <ProgressBarSkeleton />
      ) : quota ? (
        <div className="flex flex-col gap-2">
          {/* Progress bar container */}
          <div className="relative pt-6">
            {[75, 90].map((v) => (
              <ThresholdMarker key={v} value={v} />
            ))}
            <div className="et-usage-track">
              <div
                className={`et-usage-fill${isOverLimit ? " is-over" : ""}`}
                style={{ width: `${usagePercentage}%` }}
              />
            </div>
          </div>

          {/* Labels */}
          <div className="et-usage-legend">
            <strong>
              Tutoring Time Allocated: {formatHours(quota.max_usage_allowed_ms)}
            </strong>
            <div>
              <span>
                <i
                  className={`et-usage-swatch et-usage-fill${isOverLimit ? " is-over" : ""}`}
                />
                Total tutoring time utilized ={" "}
                {formatHours(quota.current_usage_ms)}
              </span>
              <span className={isOverLimit ? "is-over" : undefined}>
                <i className="et-usage-swatch et-usage-track" />
                Total tutoring time Remaining ={" "}
                {formatHours(quota.remaining_ms)}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Warning Banner - shows when usage exceeds limit */}
      {isOverLimit && (
        <div className="et-usage-warning" role="status">
          <span aria-hidden="true">!</span>
          <div>
            <h2>You've used up all your originally allocated tutoring hours</h2>
            <p>
              Tutoring service is <strong>not paused</strong>, but all overages
              will be charged additionally at the end of the billing cycle.
              Please reach out to{" "}
              <a href="mailto:billing@evertutor.ai">billing@evertutor.ai</a> for
              help.
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <label className="et-search et-usage-search">
        <Search size={18} aria-hidden="true" />
        <input
          type="search"
          aria-label="Search for a student"
          placeholder="Search for a Student"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>

      {/* Table */}
      <div className="et-usage-table">
        <table>
          <thead>
            <tr>
              <th
                className="is-sortable"
                onClick={() => handleSort("student_name")}
              >
                Student{" "}
                <SortIcon columnKey="student_name" sortConfig={sortConfig} />
              </th>
              <th>Classroom(s)</th>
              <th
                className="is-sortable"
                onClick={() => handleSort("active_tutoring_time_ms")}
              >
                Active Tutoring Time{" "}
                <SortIcon
                  columnKey="active_tutoring_time_ms"
                  sortConfig={sortConfig}
                />
              </th>
              <th>Lessons Completed</th>
              <th>Status</th>
              <th>Suspend / Resume</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>
                  <LoadingSpinner message="Loading students..." />
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td colSpan={6} className="et-usage-empty">
                  No students found
                </td>
              </tr>
            ) : (
              students.map((student) => (
                <tr key={student.user_id}>
                  <td>{student.student_name}</td>
                  <td>
                    {student.classrooms.length > 0 ? (
                      <div className="flex flex-col gap-1 items-center">
                        {student.classrooms.map((c, i) => (
                          <span key={i}>{c}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="et-usage-muted">-</span>
                    )}
                  </td>
                  <td>{formatDuration(student.active_tutoring_time_ms)}</td>
                  <td>{student.lessons_completed}</td>
                  <td>
                    <span
                      className={`et-usage-status${student.is_active ? "" : " is-inactive"}`}
                    >
                      {student.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() =>
                        handleStatusToggle(student.user_id, student.is_active)
                      }
                      title={student.is_active ? "Suspend" : "Resume"}
                      aria-label={`${student.is_active ? "Suspend" : "Resume"} ${student.student_name}`}
                      className="et-usage-toggle"
                    >
                      {student.is_active ? (
                        <Pause
                          size={18}
                          fill="currentColor"
                          aria-hidden="true"
                        />
                      ) : (
                        <Play
                          size={18}
                          fill="currentColor"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsageControlViewContent;
